import { Location } from "./forwarding";

/**
 * Waiting for the address bar to stop moving before anything reads it.
 *
 * Two things move it without warning. react-navigation publishes a stack's
 * state with its initial route first, so mounting a nested screen goes
 * through the parent path; and on a full page load that stack is lazily
 * loaded, so the parent path can be what the bar says for several frames
 * after the gate has its answers. A rule that owns the parent — an exact
 * `profile` rule, say — then matches a path nobody asked for, and the
 * forward is a page load there is no coming back from.
 *
 * So: never judge a location that has not been still for a frame.
 */

export type Scheduler = {
  schedule: (callback: () => void) => number;
  cancel: (handle: number) => void;
};

const animationFrames: Scheduler = {
  schedule: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => cancelAnimationFrame(handle),
};

/**
 * Ownership is decided on the path and the query, so those are what has to
 * hold still. A hash travels with the forward rather than deciding whether
 * there is one.
 */
const sameAddress = (a: Location, b: Location) =>
  a.pathname === b.pathname && a.search === b.search;

/**
 * Calls back once with the location, as soon as two consecutive frames read
 * the same address. Returns a cancel.
 *
 * A bar that never stops moving never gets an answer. That is the right
 * failure for a forward — no page load on an address nobody settled on — but
 * it is not free: whatever the gate was showing when it started waiting stays
 * up, and after a dropped forward that is nothing at all. Every bar people
 * actually drive stops within a frame or two, and the gate judges the first
 * still moment like any other.
 */
export function settledLocation(
  read: () => Location,
  onSettled: (location: Location) => void,
  scheduler: Scheduler = animationFrames,
): () => void {
  let previous = read();
  let handle = 0;
  let done = false;

  const step = () => {
    // A cancel that lands after the frame was already queued still has to be
    // final: this is the only place the callback can be stopped.
    if (done) {
      return;
    }

    handle = 0;

    const current = read();

    if (sameAddress(current, previous)) {
      done = true;
      onSettled(current);
      return;
    }

    previous = current;
    handle = scheduler.schedule(step);
  };

  handle = scheduler.schedule(step);

  return () => {
    done = true;

    if (handle) {
      scheduler.cancel(handle);
      handle = 0;
    }
  };
}
